import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, Mail, Building2, DollarSign } from 'lucide-react';

const InvestorInterestForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    organization: '',
    investor_type: '',
    ticket_size: '',
    message: '',
    honeypot: '',
    consent: false
  });

  const investorTypes = [
    'Angel Investor',
    'Venture Capital',
    'Family Office',
    'Private Equity',
    'Strategic Investor',
    'Other'
  ];

  const ticketSizes = [
    '$50K - $100K',
    '$100K - $250K',
    '$250K - $500K',
    '$500K - $1M',
    '$1M+'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/investor-leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Thank you for your interest!', {
          description: 'We\'ll be in touch soon with the investor pack.'
        });
        // Reset form
        setFormData({
          full_name: '',
          email: '',
          organization: '',
          investor_type: '',
          ticket_size: '',
          message: '',
          honeypot: '',
          consent: false
        });
      } else {
        toast.error('Submission failed', {
          description: data.detail || 'Please try again later.'
        });
      }
    } catch (error) {
      toast.error('Network error', {
        description: 'Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    'Detailed pitch deck with market analysis',
    'Access to financial models and projections',
    'Invitation to private Q&A sessions',
    'Early access to investment terms'
  ];

  return (
    <section id="contact" className="py-16 lg:py-24 bg-secondary/30">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <p className="uppercase tracking-[0.22em] text-xs text-muted-foreground mb-6">
            Get Involved
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold mb-6">
            Investor Interest
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Join us in building the trust infrastructure for high-value markets. Submit your information to receive the full investor pack.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-2"
          >
            <Card className="p-6 sm:p-8">
              <form onSubmit={handleSubmit} data-testid="investor-interest-form">
                <div className="space-y-6">
                  {/* Full Name */}
                  <div>
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                      className="mt-2"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-2"
                    />
                  </div>

                  {/* Organization */}
                  <div>
                    <Label htmlFor="organization">Organization</Label>
                    <Input
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      className="mt-2"
                    />
                  </div>

                  {/* Investor Type & Ticket Size */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="investor_type">Investor Type *</Label>
                      <select
                        id="investor_type"
                        name="investor_type"
                        value={formData.investor_type}
                        onChange={handleChange}
                        required
                        className="mt-2 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        <option value="">Select type...</option>
                        {investorTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="ticket_size">Ticket Size *</Label>
                      <select
                        id="ticket_size"
                        name="ticket_size"
                        value={formData.ticket_size}
                        onChange={handleChange}
                        required
                        className="mt-2 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        <option value="">Select range...</option>
                        {ticketSizes.map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <Label htmlFor="message">Message (Optional)</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="mt-2"
                      placeholder="Any specific questions or areas of interest?"
                    />
                  </div>

                  {/* Honeypot (hidden) */}
                  <input
                    type="text"
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={handleChange}
                    style={{ display: 'none' }}
                    tabIndex="-1"
                    autoComplete="off"
                  />

                  {/* Consent */}
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="consent"
                      checked={formData.consent}
                      onCheckedChange={(checked) => 
                        setFormData(prev => ({ ...prev, consent: checked }))
                      }
                      required
                    />
                    <Label htmlFor="consent" className="text-sm leading-relaxed cursor-pointer">
                      I agree to be contacted by Sovereign regarding this investment opportunity. Your information will be kept confidential.
                    </Label>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    disabled={isSubmitting || !formData.consent}
                    data-testid="investor-interest-submit-button"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Mail className="mr-2 h-4 w-4" />
                        Request Investor Pack
                      </>
                    )}
                  </Button>
                  
                  {!formData.consent && (
                    <p className="text-xs text-muted-foreground text-center">
                      Please agree to the consent terms to submit
                    </p>
                  )}
                </div>
              </form>
            </Card>
          </motion.div>

          {/* Benefits Sidebar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="p-6 h-full">
              <h3 className="text-lg font-semibold mb-4">What You'll Receive</h3>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-sm">
                    <Building2 className="h-5 w-5 text-muted-foreground" />
                    <span className="text-muted-foreground">Institutional-grade diligence materials</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <DollarSign className="h-5 w-5 text-muted-foreground" />
                    <span className="text-muted-foreground">Transparent financial projections</span>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default InvestorInterestForm;
